import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { BranchService } from '@core/services/helpers/branch.service';

import { CoreModule } from '@core/core.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RootStoreModule } from '@store/root-store.module';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { VideoPlayerComponent } from '@shared/components/video-player/video-player.component';
import { VideoCardComponent } from './shared/components/cards/video-card/video-card.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import * as Sentry from '@sentry/angular';
import { DialogModule } from '@shared/modules/dialog/dialog.module';

const BRANCH_INIT_TIMEOUT_MS = 4000;

export function initializeBranchFactory(branchService: BranchService): () => Promise<void> {
  return () => {
    const timeoutPromise = new Promise<void>(resolve =>
      setTimeout(() => {
        Sentry.addBreadcrumb({
          category: 'branch',
          message: `Branch init timed out after ${BRANCH_INIT_TIMEOUT_MS}ms`,
          level: 'warning',
        });
        resolve();
      }, BRANCH_INIT_TIMEOUT_MS)
    );
    return Promise.race([branchService.initializeBranch(), timeoutPromise]);
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    DialogModule,
    VideoPlayerComponent,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    RootStoreModule,
    CoreModule,
    VideoCardComponent,
    SpinnerComponent,
  ],
  providers: [
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeBranchFactory,
      deps: [BranchService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
